/**
 * Revisión de matrículas — sección "Revisión" del hub Inventario de Documentos.
 *
 * Responde "¿dónde está cada documento de este chasis?" en una sola pantalla:
 *   - Cobros de matrícula registrados al chasis (tabla pagomatri).
 *   - Estado de cada tipo de documento del catálogo según el blob
 *     `sucursaldocumentos` (sin recibir / recibido en qué sucursal / entregado).
 *   - Entregas al cliente (blob `cajeromatriculaentregada`): quién lo retiró,
 *     cuándo, en qué sucursal y qué cajero lo entregó, con corrección de datos,
 *     eliminación de la recepción y el PDF de recepción.
 *
 * Backend (transferenciabackend.py): todo sale de /api/consultarmatricula, que
 * devuelve el inventario completo (matriculas = lista de chasis, matriculas2 =
 * producto con los blobs, pagomatri = cobros); la selección se resuelve en el
 * cliente. Editar usa /api/transferenciabackend_actualizarmatricula (parchea
 * la entrega por `documentoentregado`) y eliminar
 * /api/transferenciabackend_eliminarrecepcion (quita la entrega y vuelve a
 * marcar sus documentos como no entregados). Tras cualquiera de las dos se
 * recarga la consulta completa, porque los blobs se reconstruyen en el server.
 *
 * Se monta embebida en transferenciadocumentos.js: sin cabecera propia.
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useMaterialUIController, useGlobalMDAlert } from "context";
import { FP, fpInputSx, StatusPill, EstadoVacio, ConfirmarDialog } from "components/financepro";
import ReporteRecepcionMatricula from "components/ReporteRecepcionMatricula";
import PDFModal from "components/PDFModal";
import { useApi } from "lib/api";
import {
  parseDocumentos,
  parseEntregas,
  estadoPorTipo,
  labelTipo,
  codigoSucursal,
  nombreSucursal,
  useTiposDocumento,
} from "lib/documentosvehiculo";

// La consulta no trae marca/modelo (solo chasis, placa, suplidor y ubicación),
// así que el buscador indexa lo que hay.
const filtrarChasis = createFilterOptions({
  stringify: (o) => `${o.chasis} ${o.placa || ""} ${o.suplidor || ""} ${o.localizacion || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};
// El tema (assets/theme/components/table/tableHead) pone display:block al
// TableHead y desalinea las columnas.
const tableHeadSx = { display: "table-header-group", p: 0, borderRadius: 0 };
const celda = { py: 0.6, px: 1, fontSize: "0.82rem", borderColor: FP.color.border };
const celdaCabecera = { ...celda, fontWeight: 800 };
const chasisSx = { fontFamily: "monospace", fontWeight: 700 };

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}
SectionTitle.propTypes = {
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
  color: PropTypes.string.isRequired,
};

/** Par etiqueta/valor de las tarjetas de entrega. */
function Dato({ etiqueta, children, mono }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: "0.68rem",
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: FP.color.textMuted,
        }}
      >
        {etiqueta}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.85rem",
          color: FP.color.textPrimary,
          fontWeight: mono ? 700 : 500,
          fontFamily: mono ? "monospace" : undefined,
        }}
      >
        {children || "—"}
      </Typography>
    </Box>
  );
}
Dato.propTypes = {
  etiqueta: PropTypes.string.isRequired,
  children: PropTypes.node,
  mono: PropTypes.bool,
};
Dato.defaultProps = { children: null, mono: false };

/** "2-Veron" → "Veron (2)"; "2" → "Sucursal 2". */
const etiquetaSucursal = (valor) => {
  const codigo = codigoSucursal(valor);
  const nombre = nombreSucursal(valor);
  if (!codigo && !nombre) return "";
  if (nombre) return `${nombre} (${codigo})`;
  return `Sucursal ${codigo}`;
};

const ENTREGA_VACIA = {
  lote: "",
  nombres: "",
  apellidos: "",
  cedula: "",
  fecha: "",
  sucursal: "",
  cajero: "",
  localizacion: "",
  documentoentregado: "",
};

export default function ConsultaMatriculas() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [controller] = useMaterialUIController();
  const { roleWeb } = controller;
  const { tipos } = useTiposDocumento();

  const [inventario, setInventario] = useState({ matriculas: [], matriculas2: [], pagomatri: [] });
  const [cargando, setCargando] = useState(false);
  const [sel, setSel] = useState(null);

  // Edición de una entrega: el body que espera actualizarmatricula.
  const [edicion, setEdicion] = useState(null);
  const [guardando, setGuardando] = useState(false);
  // Entrega a eliminar (solo hace falta su documentoentregado).
  const [aEliminar, setAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  const [pdfUrl, setPdfUrl] = useState("");

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/consultarmatricula", {});
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo consultar el inventario", color: "error" });
        return;
      }
      setInventario({
        matriculas: Array.isArray(data.matriculas) ? data.matriculas : [],
        matriculas2: Array.isArray(data.matriculas2) ? data.matriculas2 : [],
        pagomatri: Array.isArray(data.pagomatri) ? data.pagomatri : [],
      });
    } finally {
      setCargando(false);
    }
  }, [post, showAlert]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Opciones del buscador: la lista de chasis (matriculas) enriquecida con la
  // fila de producto (matriculas2) para la segunda línea del desplegable.
  const opciones = useMemo(() => {
    const porChasis = new Map(inventario.matriculas2.map((f) => [f.chasis, f]));
    return inventario.matriculas
      .map((m) => m.label)
      .filter(Boolean)
      .map((chasis) => ({ ...(porChasis.get(chasis) || {}), chasis }));
  }, [inventario]);

  // La selección se guarda por chasis: al recargar tras editar/eliminar, la
  // fila nueva reemplaza a la vieja sin perder lo elegido.
  const chasis = sel?.chasis || "";
  const fila = useMemo(
    () => inventario.matriculas2.find((f) => f.chasis === chasis) || null,
    [inventario, chasis]
  );
  const cobros = useMemo(
    () => inventario.pagomatri.filter((p) => p.chasis === chasis),
    [inventario, chasis]
  );
  const entregas = useMemo(
    () => parseEntregas(fila?.cajeromatriculaentregada).map((e, i) => ({ ...e, id: i })),
    [fila]
  );
  // Estado por tipo del catálogo, más los documentos del blob que no estén en
  // el catálogo (tipos retirados): nada del blob queda sin mostrar.
  const documentos = useMemo(() => {
    const blob = fila?.sucursaldocumentos;
    const extras = parseDocumentos(blob)
      .map((d) => d.documento)
      .filter((t) => t && !tipos.some((tipo) => tipo.value === t));
    const todos = [...tipos.map((t) => t.value), ...new Set(extras)];
    const estado = estadoPorTipo(blob, todos);
    return todos.map((tipo) => ({ tipo, ...estado[tipo] }));
  }, [fila, tipos]);

  // Mismo criterio que la pantalla anterior: solo administradores gestionan
  // entregas, y solo sobre entregas con cliente.
  const puedeGestionar = ["AdmG", "Administrador"].includes(String(roleWeb || "").trim());

  // `lote` viaja aparte del resto (el endpoint lo lee del cuerpo, no de
  // `datos`): identifica la entrega exacta cuando la hizo el lote nuevo. Sin
  // él, el servidor empata por `documentoentregado` como siempre.
  const abrirEdicion = (entrega) =>
    setEdicion({
      lote: entrega.lote || "",
      nombres: entrega.nombres || "",
      apellidos: entrega.apellidos || "",
      cedula: entrega.cedula || "",
      fecha: entrega.fecha || "",
      sucursal: entrega.sucursalentrega || "",
      cajero: entrega.cajero || "",
      localizacion: entrega.localizacion || "",
      documentoentregado: entrega.documentoentregado || "",
    });

  const edicionCompleta =
    Boolean(edicion) &&
    [
      edicion?.nombres,
      edicion?.apellidos,
      edicion?.cedula,
      edicion?.fecha,
      edicion?.sucursal,
    ].every((v) => String(v || "").trim().length > 0);

  const guardarEdicion = async () => {
    setGuardando(true);
    try {
      const { lote, ...datos } = edicion;
      const { ok, data } = await post("/api/transferenciabackend_actualizarmatricula", {
        chasis,
        datos,
        lote,
      });
      if (!ok) {
        showAlert({ message: `Error al actualizar: ${data.Error || ""}`, color: "error" });
        return;
      }
      showAlert({ message: data.OK || "Datos actualizados correctamente", color: "success" });
      setEdicion(null);
      cargar();
    } finally {
      setGuardando(false);
    }
  };

  const eliminarRecepcion = async () => {
    setEliminando(true);
    try {
      const { ok, data } = await post("/api/transferenciabackend_eliminarrecepcion", {
        chasis,
        documentoentregado: aEliminar?.documentoentregado || "",
        lote: aEliminar?.lote || "",
      });
      if (!ok) {
        showAlert({ message: `Error al eliminar recepción: ${data.Error || ""}`, color: "error" });
        return;
      }
      showAlert({ message: "Recepción eliminada correctamente", color: "success" });
      setAEliminar(null);
      cargar();
    } finally {
      setEliminando(false);
    }
  };

  const generarPdf = (entrega) => {
    const datosMatricula = {
      chasis,
      nombres: entrega.nombres || "",
      apellidos: entrega.apellidos || "",
      cedula: entrega.cedula || "",
      fecha: entrega.fecha || "",
      sucursal: entrega.sucursalentrega || "",
      cajero: entrega.cajero || "",
      localizacion: entrega.localizacion || "",
      documentoentregado: entrega.documentoentregado || "",
    };
    // El reporte muestra un solo pago: el primero registrado al chasis.
    const pago = cobros[0];
    const datosPago = pago
      ? {
          valor: pago.valor || "",
          fecha: pago.fecha || "",
          recibido: pago.recibido || "",
          descripcion: pago.descripcion || "",
        }
      : null;
    setPdfUrl(ReporteRecepcionMatricula.generarReporteParaModal(datosMatricula, datosPago));
  };

  const cerrarPdf = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl("");
  };

  // El blob dice dónde está cada documento (lo escribe la recepción DGII y
  // lo mueve la recepción de envíos). "Sin registrar" = nadie lo ubicó aún:
  // matrículas recibidas antes de que la recepción registrara documentos, o
  // copias que no se marcaron al recibir.
  const pillDocumento = (d) => {
    if (d.entregado) return <StatusPill label="Entregado al cliente" variant="success" />;
    if (d.presente) return <StatusPill label="En sucursal" variant="info" />;
    return <StatusPill label="Sin registrar" variant="neutral" />;
  };

  const enInventario = String(fila?.inventario || "").toUpperCase() === "S";

  return (
    <Box>
      {/* Búsqueda */}
      <Box sx={sectionBox}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <SectionTitle icon={SearchOutlinedIcon} color={FP.color.accentBlue}>
            Búsqueda
          </SectionTitle>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              startIcon={
                cargando ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
              }
              onClick={cargar}
              disabled={cargando}
              sx={btnOutline(FP.color.navy)}
            >
              Actualizar
            </Button>
            {sel && (
              <Button
                startIcon={<RestartAltIcon />}
                onClick={() => setSel(null)}
                sx={btnOutline(FP.color.navy)}
              >
                Limpiar
              </Button>
            )}
          </Stack>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={5}>
            <Autocomplete
              autoHighlight
              selectOnFocus
              handleHomeEndKeys
              filterOptions={filtrarChasis}
              options={opciones}
              loading={cargando}
              value={sel}
              getOptionLabel={(o) => (typeof o === "string" ? o : o.chasis)}
              isOptionEqualToValue={(o, v) => o.chasis === v.chasis}
              onChange={(_, v) => setSel(v)}
              renderOption={(props, o) => (
                <li {...props} key={o.chasis}>
                  <Box>
                    <Typography sx={{ ...chasisSx, fontSize: "0.85rem" }}>{o.chasis}</Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                      {[
                        o.placa && `Placa ${o.placa}`,
                        o.suplidor,
                        o.localizacion && o.localizacion !== "N" && `Ubic. ${o.localizacion}`,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "Sin placa registrada"}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Chasis, placa o suplidor" sx={fpInputSx} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={7} sx={{ display: "flex", alignItems: "center" }}>
            {fila && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {enInventario ? (
                  <Chip
                    label="Matrícula en inventario"
                    sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
                  />
                ) : (
                  <Chip
                    label="Matrícula sin recibir"
                    sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                  />
                )}
                {fila.placa && (
                  <Chip
                    label={`Placa ${fila.placa}`}
                    sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }}
                  />
                )}
                {fila.endoso && (
                  <Chip
                    label={`Endoso ${fila.endoso}`}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                )}
                {fila.localizacion && fila.localizacion !== "N" && (
                  <Chip
                    label={`Ubicación ${fila.localizacion}`}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                )}
                {fila.suplidor && (
                  <Chip
                    label={fila.suplidor}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                )}
                {fila.consignado && (
                  <Chip
                    label="Consignado"
                    sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                  />
                )}
              </Stack>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Sin chasis: instrucción en vez de pantalla en blanco */}
      {!fila && (
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <EstadoVacio
            icono={SearchOutlinedIcon}
            titulo="Elige un chasis para revisarlo"
            subtitulo={
              cargando
                ? "Cargando el inventario de documentos…"
                : `Busca por chasis, placa o suplidor entre ${opciones.length} vehículo(s) para ver sus cobros de matrícula, dónde está cada documento y las entregas al cliente.`
            }
          />
        </Box>
      )}

      {fila && (
        <>
          {/* Cobros de matrícula */}
          <Box sx={sectionBox}>
            <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.success}>
              Cobros de matrícula ({cobros.length})
            </SectionTitle>
            {cobros.length === 0 ? (
              <Typography sx={{ fontSize: "0.82rem", color: FP.color.textMuted }}>
                Este chasis no tiene cobro de matrícula registrado.
              </Typography>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 640 }}>
                  <TableHead sx={tableHeadSx}>
                    <TableRow>
                      <TableCell sx={celdaCabecera}>Cliente</TableCell>
                      <TableCell sx={{ ...celdaCabecera, textAlign: "right" }}>Valor</TableCell>
                      <TableCell sx={celdaCabecera}>Fecha</TableCell>
                      <TableCell sx={celdaCabecera}>Sucursal de matrícula</TableCell>
                      <TableCell sx={celdaCabecera}>Matrícula</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cobros.map((c) => (
                      <TableRow key={`${c.fecha}|${c.valor}|${c.nombres}|${c.apellidos}`}>
                        <TableCell sx={{ ...celda, fontWeight: 700 }}>
                          {[c.nombres, c.apellidos].filter(Boolean).join(" ") || "—"}
                        </TableCell>
                        <TableCell sx={{ ...celda, textAlign: "right", fontWeight: 700 }}>
                          RD$ {fmtMoney(c.valor)}
                        </TableCell>
                        <TableCell sx={celda}>{c.fecha || "—"}</TableCell>
                        <TableCell sx={celda}>{c.descripcion || "—"}</TableCell>
                        <TableCell sx={celda}>
                          {String(c.recibido || "").toUpperCase() === "S" ? (
                            <StatusPill label="Recibida" variant="success" />
                          ) : (
                            <StatusPill label="Pendiente" variant="warning" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>

          {/* Documentos */}
          <Box sx={sectionBox}>
            <SectionTitle icon={DescriptionOutlinedIcon} color={FP.color.accentBlue}>
              Documentos
            </SectionTitle>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 520 }}>
                <TableHead sx={tableHeadSx}>
                  <TableRow>
                    <TableCell sx={celdaCabecera}>Documento</TableCell>
                    <TableCell sx={celdaCabecera}>Estado</TableCell>
                    <TableCell sx={celdaCabecera}>Ubicación</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documentos.map((d) => (
                    <TableRow key={d.tipo} sx={{ opacity: d.presente ? 1 : 0.6 }}>
                      <TableCell sx={{ ...celda, fontWeight: 700 }}>{labelTipo(d.tipo)}</TableCell>
                      <TableCell sx={celda}>{pillDocumento(d)}</TableCell>
                      <TableCell sx={celda}>
                        {d.presente ? etiquetaSucursal(d.sucursalrecep) || "—" : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                  {documentos.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        sx={{ ...celda, textAlign: "center", color: FP.color.textMuted }}
                      >
                        Sin tipos de documento configurados
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>

          {/* Entregas al cliente */}
          <Box sx={{ ...sectionBox, mb: 0 }}>
            <SectionTitle icon={AssignmentTurnedInOutlinedIcon} color={FP.color.success}>
              Entregas al cliente ({entregas.length})
            </SectionTitle>
            {entregas.length === 0 ? (
              <EstadoVacio
                compacto
                icono={AssignmentTurnedInOutlinedIcon}
                titulo="Sin entregas al cliente"
                subtitulo="Ningún documento de este chasis se ha entregado todavía. Las entregas se registran en la sección Entrega."
              />
            ) : (
              <Grid container spacing={1.6}>
                {entregas.map((e) => {
                  const conCliente = String(e.nombres || "").length > 0;
                  return (
                    <Grid item xs={12} md={6} key={e.id}>
                      <Box
                        sx={{
                          border: `1px solid ${FP.color.border}`,
                          borderRadius: 2,
                          p: 1.8,
                          height: "100%",
                          bgcolor: FP.color.surface,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.4,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                            {String(e.documentoentregado || "")
                              .split(",")
                              .map((t) => t.trim())
                              .filter(Boolean)
                              .map((t) => (
                                <Chip
                                  key={t}
                                  size="small"
                                  label={labelTipo(t)}
                                  sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
                                />
                              ))}
                            {!e.documentoentregado && (
                              <StatusPill label="Sin documento" variant="neutral" />
                            )}
                          </Stack>
                          <Typography
                            sx={{
                              fontSize: "0.78rem",
                              fontWeight: 700,
                              color: FP.color.textSecondary,
                            }}
                          >
                            {e.fecha || "Sin fecha"}
                          </Typography>
                        </Box>
                        <Grid container spacing={1.2}>
                          <Grid item xs={12} sm={6}>
                            <Dato etiqueta="Cliente">
                              {[e.nombres, e.apellidos].filter(Boolean).join(" ")}
                            </Dato>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Dato etiqueta="Cédula" mono>
                              {e.cedula}
                            </Dato>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Dato etiqueta="Entregado en">
                              {etiquetaSucursal(e.sucursalentrega)}
                            </Dato>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Dato etiqueta="Entregado por">{e.cajero}</Dato>
                          </Grid>
                          {e.localizacion && (
                            <Grid item xs={12} sm={6}>
                              <Dato etiqueta="Ubicación">{e.localizacion}</Dato>
                            </Grid>
                          )}
                        </Grid>
                        {puedeGestionar && conCliente && (
                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mt: "auto" }}
                          >
                            <Button
                              size="small"
                              startIcon={<PictureAsPdfIcon />}
                              onClick={() => generarPdf(e)}
                              sx={btnOutline(FP.color.navy)}
                            >
                              PDF de recepción
                            </Button>
                            <Button
                              size="small"
                              startIcon={<EditOutlinedIcon />}
                              onClick={() => abrirEdicion(e)}
                              sx={btnOutline(FP.color.navy)}
                            >
                              Editar datos
                            </Button>
                            <Button
                              size="small"
                              startIcon={<DeleteOutlineIcon />}
                              onClick={() => setAEliminar(e)}
                              sx={btnOutline("#C62828")}
                            >
                              Eliminar recepción
                            </Button>
                          </Stack>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
            {!puedeGestionar && entregas.length > 0 && (
              <Typography sx={{ mt: 1.2, fontSize: "0.75rem", color: FP.color.textMuted }}>
                Solo un administrador puede corregir o eliminar una entrega.
              </Typography>
            )}
          </Box>
        </>
      )}

      {/* Edición de datos de la entrega */}
      <Dialog
        open={edicion != null}
        onClose={guardando ? undefined : () => setEdicion(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, boxShadow: FP.shadow.md } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
          Editar datos de entrega
          <Typography component="span" sx={{ ...chasisSx, display: "block", fontSize: "0.8rem" }}>
            {chasis}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombres"
                value={edicion?.nombres || ""}
                onChange={(ev) =>
                  setEdicion({ ...(edicion || ENTREGA_VACIA), nombres: ev.target.value })
                }
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Apellidos"
                value={edicion?.apellidos || ""}
                onChange={(ev) =>
                  setEdicion({ ...(edicion || ENTREGA_VACIA), apellidos: ev.target.value })
                }
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cédula"
                value={edicion?.cedula || ""}
                onChange={(ev) =>
                  setEdicion({ ...(edicion || ENTREGA_VACIA), cedula: ev.target.value })
                }
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Fecha de entrega"
                value={edicion?.fecha || ""}
                onChange={(ev) =>
                  setEdicion({ ...(edicion || ENTREGA_VACIA), fecha: ev.target.value })
                }
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            {/* Sucursal y cajero los fijó quien entregó: se muestran, no se editan. */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                disabled
                label="Sucursal"
                value={edicion?.sucursal || ""}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                disabled
                label="Cajero"
                value={edicion?.cajero || ""}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                disabled
                label="Documento entregado"
                value={labelTipo(edicion?.documentoentregado || "")}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEdicion(null)}
            disabled={guardando}
            sx={btnOutline(FP.color.navy)}
          >
            Cancelar
          </Button>
          <Button
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={guardarEdicion}
            disabled={guardando || !edicionCompleta}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Guardar cambios
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmarDialog
        abierto={aEliminar != null}
        titulo="Eliminar recepción"
        destructivo
        mensaje={`Se eliminará la entrega de "${labelTipo(
          aEliminar?.documentoentregado || ""
        )}" del chasis ${chasis} y esos documentos volverán a quedar como no entregados. Esta acción no se puede deshacer.`}
        textoConfirmar="Eliminar"
        cargando={eliminando}
        onCerrar={() => setAEliminar(null)}
        onConfirmar={eliminarRecepcion}
      />

      <PDFModal
        open={Boolean(pdfUrl)}
        onClose={cerrarPdf}
        pdfUrl={pdfUrl}
        title="Recepción de Matrícula"
        showDownloadButton
        showPrintButton
      />
    </Box>
  );
}
